import type { CanonicalGeographyFeature, ConvertGeoJSONToGeographyInput } from '../state/api/organisationEngineSlice';

/** Discriminator values for `GeographyOutcome.kind`. Defined once here so
 *  consumers (hook impl, tests, UI) reference them by name rather than
 *  repeating the literal strings — rename a member and tsc catches every site. */
export enum GeographyOutcomeKind {
    Created = 'created',
    NoGeographyAvailable = 'no_geography_available',
    Failed = 'failed',
}

/** Which of the three geography sub-steps produced a `Failed` outcome. */
export enum GeographyStep {
    Fetch = 'fetch',
    Convert = 'convert',
    Create = 'create',
}

export type GeographyFailure = {
    step: GeographyStep;
    /** HTTP status when the underlying error was an RTK Query
     *  FetchBaseQueryError with a numeric status. Absent for plain
     *  network/parse failures. */
    status?: number;
    /** Best-effort human-readable message: engine's `data.error` /
     *  `data.message` when present, else the caught Error's `message`.
     *  Absent when nothing readable could be extracted. */
    message?: string;
};

export type GeographyOutcome =
    | { kind: GeographyOutcomeKind.Created }
    | { kind: GeographyOutcomeKind.NoGeographyAvailable }
    | { kind: GeographyOutcomeKind.Failed; failure: GeographyFailure };

/** Discriminator values for `SiteImportOutcome.kind`. */
export enum SiteImportOutcomeKind {
    Imported = 'imported',
    Skipped = 'skipped', // 409: site already existed in engine
    Failed = 'failed',
}

export type SiteImportOutcome =
    | { kind: SiteImportOutcomeKind.Imported; geography: GeographyOutcome }
    // Skipped + Failed don't carry a geography outcome — we never reached the
    // pipeline. Excluded explicitly so adding a new SiteImportOutcomeKind
    // member surfaces here at the type level.
    | { kind: Exclude<SiteImportOutcomeKind, SiteImportOutcomeKind.Imported> };

export type ImportRowResult = {
    projectId: string;
    projectName: string;
    outcome: SiteImportOutcome;
};

export type ImportResult = {
    imported: number;
    /** Site created but the geography step failed — a broken state a
     *  re-run can recover. Derived from row outcomes:
     *  Imported && geography.kind === Failed. */
    geographyFailed: number;
    skipped: number;
    failed: number;
    rows: ImportRowResult[];
};

/** A project as the caller knows it — pulled from `customerProjectsInBC`
 *  on the Go GraphQL side. The hook only reads these two fields. */
export type Project = {
    projectId: string;
    projectName: string;
};

/** Narrow projection of the Go GraphQL `getProjectSummary().sites[]` shape —
 *  only the geometry coordinates are used by the import pipeline. */
export type ProjectGeometrySite = {
    geometry?: { type?: string; coordinates?: [number, number][][] } | null;
};

/** A site as returned by the engine's `POST /org/{bcId}/sites`. Only the
 *  generated `id` is consumed downstream (passed to the geography write). */
export type CreatedSite = { id: string };

/** Row returned by the engine's GraphQL preload query — the projection
 *  `useImportSites` actually consumes. `hasGeography` is derived by the
 *  caller from the GraphQL response's `geography` field being non-null. */
export type ExistingDraftSite = {
    businessCentralId: string;
    siteId: string;
    hasGeography: boolean;
};

export type UseImportSitesOptions = {
    createSite: (request: { bcId: string; body: { name: string; business_central_id: string } }) => Promise<CreatedSite>;
    fetchProjectSites: (projectId: string) => Promise<ProjectGeometrySite[]>;
    convertGeoJSONToGeography: (input: ConvertGeoJSONToGeographyInput) => Promise<CanonicalGeographyFeature>;
    createGeography: (request: { bcId: string; siteId: string; body: CanonicalGeographyFeature }) => Promise<unknown>;
    /** Fetches the org's existing draft RI sites so import can decide,
     *  per project, whether to create a fresh site or attempt geography
     *  recovery on an already-existing draft. Called once per batch. */
    fetchExistingDraftSites: (bcId: string) => Promise<ExistingDraftSite[]>;
};
